// ReenviarLinkModal.tsx

import { useState } from 'react';
import {
  Alert,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';

export interface ReenviarLinkModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (horasExtension?: number) => Promise<void>;
  noTramite: string;
  cedula: string;
  loading?: boolean;
  message?: string;
  isError?: boolean;
  link?: string | null;
  caducidad?: string | null;
}

const copyToClipboard = async (text: string): Promise<boolean> => {
  try {
    if (navigator.clipboard && window.isSecureContext) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    // continúa con el fallback
  }

  try {
    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.style.position = 'fixed';
    textarea.style.opacity = '0';
    document.body.appendChild(textarea);
    textarea.focus();
    textarea.select();
    const ok = document.execCommand('copy');
    document.body.removeChild(textarea);
    return ok;
  } catch {
    return false;
  }
};

export const ReenviarLinkModal = ({
  open,
  onClose,
  onConfirm,
  noTramite,
  cedula,
  loading,
  message,
  isError,
  link,
  caducidad,
}: ReenviarLinkModalProps) => {
  const [horasExtension, setHorasExtension] = useState<string>('24');
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);

  const reset = () => {
    setHorasExtension('24');
    setError('');
    setCopied(false);
  };

  const handleClose = () => {
    if (loading) return;
    reset();
    onClose();
  };

  const handleConfirm = async () => {
    const horas = Number.parseInt(horasExtension, 10);
    if (Number.isNaN(horas) || horas <= 0) {
      setError('Ingresa un número de horas de extensión válido (mayor a 0).');
      return;
    }
    setError('');
    setCopied(false);
    await onConfirm(horas);
  };

  const handleCopy = async () => {
    if (!link) return;
    const ok = await copyToClipboard(link);
    setCopied(ok);
    if (ok) {
      setTimeout(() => setCopied(false), 2500);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
      <DialogTitle sx={{ pr: 6 }}>
        Reenviar link de firma
        <IconButton
          aria-label="Cerrar"
          onClick={handleClose}
          disabled={loading}
          sx={{ position: 'absolute', right: 12, top: 12 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <DialogContentText sx={{ mb: 2 }}>
          Se reenviará el link de firma del trámite{' '}
          <strong>{noTramite}</strong> al firmante con cédula{' '}
          <strong>{cedula}</strong>.
        </DialogContentText>

        <Stack spacing={1} sx={{ mt: 2 }}>
          <Typography variant="subtitle2">
            Horas de extensión (opcional)
          </Typography>
          <TextField
            type="number"
            size="small"
            fullWidth
            value={horasExtension}
            onChange={(e) => setHorasExtension(e.target.value)}
            disabled={loading}
            inputProps={{ min: 1 }}
            placeholder="Ej: 24"
          />
        </Stack>

        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}

        {message && !error && (
          <Alert severity={isError ? 'error' : 'success'} sx={{ mt: 2 }}>
            {message}
          </Alert>
        )}
        {link && (
          <Stack spacing={1.5} sx={{ mt: 2 }}>
            <Typography variant="subtitle2">Nuevo link generado</Typography>
            <TextField
              size="small"
              fullWidth
              multiline
              maxRows={3}
              value={link}
              inputProps={{ readOnly: true }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <Tooltip title={copied ? '¡Copiado!' : 'Copiar link'}>
                      <IconButton
                        aria-label="Copiar link"
                        onClick={handleCopy}
                        color={copied ? 'success' : 'default'}
                        edge="end"
                      >
                        {copied ? <CheckIcon /> : <ContentCopyIcon />}
                      </IconButton>
                    </Tooltip>
                  </InputAdornment>
                ),
              }}
              sx={{
                '& .MuiInputBase-input': { fontSize: '0.75rem' },
              }}
            />
            {caducidad && (
              <Typography variant="body2" color="text.secondary">
                El link caduca el:{' '}
                <Chip
                  label={caducidad}
                  size="small"
                  color="warning"
                  variant="outlined"
                />
              </Typography>
            )}
            <Typography variant="caption" color="text.secondary">
              Puedes copiar el link y enviarlo por un canal interno.
            </Typography>
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          Cerrar
        </Button>
        {link && (
          <Button
            variant={copied ? 'outlined' : 'contained'}
            color="success"
            startIcon={copied ? <CheckIcon /> : <ContentCopyIcon />}
            onClick={handleCopy}
            disabled={loading}
          >
            {copied ? '¡Copiado!' : 'Copiar link'}
          </Button>
        )}
        {!link && (
          <Button
            variant="contained"
            color="primary"
            onClick={handleConfirm}
            disabled={loading}
          >
            {loading ? 'Enviando...' : 'Reenviar'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default ReenviarLinkModal;
